import { VENDORED_DEPENDENCY_DETECTION } from '../../../../shared/featureFlags/flagNames';
import {
  commonLinuxArchs,
  FETCHER_DROPDOWN_OPTIONS,
  isLinuxFetcher,
  linuxDistrosByFetcher,
} from '../../../../shared/fetchers';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import FormLabel from '../../generic/Forms/FormLabel';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import Select from '../../generic/Select/Select';

import { ModalAddDependencyAlert } from './ModalAddDependencyAlert';
import type { LocatorModalData } from './types';

type AddLocatorDependencyProps = {
  onChange: (dep: Partial<LocatorModalData>) => void;
  dependencyToAdd: Partial<LocatorModalData>;
  isDisabled: boolean;
};

const selectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    height: 43,
    minHeight: 43,
  }),
};

const AddLocatorDependency = ({ isDisabled, onChange, dependencyToAdd }: AddLocatorDependencyProps) => {
  const onAttributeChange = (attr: keyof LocatorModalData | 'error', value: string) => {
    const changes: Partial<LocatorModalData> = {
      ...dependencyToAdd,
      kind: 'locator',
      [attr]: value,
    };

    if (attr === 'fetcher') {
      changes.distro = undefined;
    }

    onChange(changes);
  };

  // this feature check should not be needed; but just in case someting slips through
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const isVendored = isVendoredDependenciesEnabled && (dependencyToAdd.vendoredPath !== undefined);

  return (
    <div className="locator-panel">
      <p className="locator-panel__title">
        Search for your packages across the web
      </p>
      <ModalAddDependencyAlert dependencyToAdd={dependencyToAdd} />
      <div className="locator-panel__body">
        <div className="locator-panel__body__section locator-panel__body__name-version">
          <FormLabel
            label="Package manager"
            requiredText="(Required)"
          >
            <Select
              id="fetcher"
              placeholder="Select package manager..."
              options={FETCHER_DROPDOWN_OPTIONS}
              isDisabled={isDisabled}
              value={FETCHER_DROPDOWN_OPTIONS.find((opt) => opt.value === dependencyToAdd.fetcher)}
              onChange={(e) => {
                if (e) {
                  onAttributeChange('fetcher', e.value);
                }
              }}
              styles={selectStyles}
            />
          </FormLabel>
        </div>
        {isVendored && (
          <div className="locator-panel__body__section locator-panel__body__name-version">
            <LabeledTextInput
              labelVariant="legacy"
              type="text"
              label="Path to Vendored Dependency"
              value={dependencyToAdd.vendoredPath}
              placeholder="/"
              isRequired
              isDisabled={isDisabled}
              onChange={n => onAttributeChange('vendoredPath', n)}
            />
          </div>
        )}
        <div className="locator-panel__body__section locator-panel__body__name-version">
          <LabeledTextInput
            labelVariant="legacy"
            type="text"
            label="Name"
            value={dependencyToAdd.packageName || ''}
            placeholder="dependency"
            isRequired
            isDisabled={isDisabled}
            onChange={n => onAttributeChange('packageName', n)}
          />
          <LabeledTextInput
            labelVariant="legacy"
            type="text"
            label="Revision"
            value={dependencyToAdd.revision || ''}
            placeholder="1.2.3"
            isRequired
            isDisabled={isDisabled}
            onChange={r => onAttributeChange('revision', r)}
          />
        </div>
        { isLinuxFetcher(dependencyToAdd.fetcher) &&
          <div className="locator-panel__body__section locator-panel__body__name-version">
            <FormLabel
              label="Distro"
              requiredText="(Required)"
            >
              <Select
                isCreatable
                id="distro"
                placeholder="Select linux distribution..."
                options={linuxDistrosByFetcher[dependencyToAdd.fetcher]}
                isDisabled={isDisabled}
                value={{
                  value: dependencyToAdd.distro,
                  label: linuxDistrosByFetcher[dependencyToAdd.fetcher]
                    .find((opt) => opt.value === dependencyToAdd.distro)?.label ?? dependencyToAdd.distro,
                }}
                onChange={(e) => {
                  if (e && e.value) {
                    onAttributeChange('distro', e.value);
                  }
                }}
                styles={selectStyles}
              />
            </FormLabel>
            <LabeledTextInput
              labelVariant="legacy"
              type="text"
              label="Release"
              value={dependencyToAdd.distroRelease || ''}
              placeholder="23.10"
              isRequired
              isDisabled={isDisabled}
              onChange={r => onAttributeChange('distroRelease', r)}
            />
            <FormLabel
              label="Architecture"
              requiredText="(Required)"
            >
              <Select
                isCreatable
                id="arch"
                placeholder="Select CPU architecture..."
                options={commonLinuxArchs.map(a => ({ value: a, label: a }))}
                isDisabled={isDisabled}
                value={{
                  value: dependencyToAdd.arch,
                  label: dependencyToAdd.arch,
                }}
                onChange={(e) => {
                  if (e && e.value) {
                    onAttributeChange('arch', e.value);
                  }
                }}
                styles={selectStyles}
              />
            </FormLabel>
          </div>}
      </div>
    </div>
  );
};

export default AddLocatorDependency;
